import * as Dialog from '@radix-ui/react-dialog';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCloseOnBack } from '@/hooks/useCloseOnBack';

const actionClass =
  'h-11 flex-1 cursor-pointer text-[17px]/[22px] tracking-[-0.41px] text-alert-action active:bg-alert-pressed';

interface Props {
  isOpen: boolean;
  title: string;
  message?: string;
  confirmLabel: string;
  hideCancel?: boolean;
  isPending?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const AlertDialog: React.FC<Props> = ({
  isOpen,
  title,
  message,
  confirmLabel,
  hideCancel = false,
  isPending = false,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();

  useCloseOnBack(isOpen, onClose);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(isNextOpen) => !isNextOpen && onClose()}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] animate-fade-in bg-alert-overlay" />
        <Dialog.Content
          aria-describedby={undefined}
          onInteractOutside={(event) => event.preventDefault()}
          className="fixed top-1/2 left-1/2 z-[70] w-[270px] -translate-x-1/2 -translate-y-1/2 animate-alert-in overflow-hidden rounded-[14px] bg-alert-surface font-system outline-none backdrop-blur-[22px]"
        >
          <div className="flex flex-col gap-0.5 px-4 py-5 text-center">
            <Dialog.Title className="text-[17px]/[22px] font-semibold tracking-[-0.41px] text-foreground">
              {title}
            </Dialog.Title>
            {message && (
              <p className="text-[13px]/[18px] tracking-[-0.08px] text-foreground">
                {message}
              </p>
            )}
          </div>

          <div className="flex border-t border-alert-border">
            {!hideCancel && (
              <button type="button" onClick={onClose} className={actionClass}>
                {t('global.cancel')}
              </button>
            )}
            <button
              type="button"
              disabled={isPending}
              onClick={onConfirm}
              className={`font-semibold ${hideCancel ? '' : 'border-l border-alert-border'} ${actionClass}`}
            >
              {confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
